"use client";

import type * as React from "react";
import type { GetVariantIdsForTenant } from "@tilli-internal/tenant-data";
import type conEdison from "@tilli-internal/tenant-data/tenants/con-edison";
import { compose, cva, type VariantProps } from "cva";
import {
  BaseButton,
  type BaseButtonProps,
  baseButtonVariants,
} from "#ui-atoms/atoms/button/button";
import { cn } from "#ui-atoms/lib/utils";

// import { lexend } from "../tenant/config/con-edison.fonts"; // TODO: import this once fonts live in this package

type ConEdisonTenantVariants = Exclude<
  GetVariantIdsForTenant<typeof conEdison>,
  "con-edison"
>;

const conEdisonButtonVariants = compose(
  baseButtonVariants,
  cva({
    base: "disabled:border-tenant-con-edison-website-gray disabled:bg-tenant-con-edison-website-gray disabled:text-tenant-con-edison-website-border rounded-full",
    variants: {
      variant: {
        default: "",
        secondary: "",
        outline: "",
        ghost: "",
        destructive: "",
        "destructive-outline": "",
        link: "",
      },
      size: {
        default: "",
        xs: "",
        sm: "",
        lg: "",
        xl: "",
        icon: "",
        "icon-xs": "",
        "icon-sm": "",
        "icon-lg": "",
        "icon-xl": "",
      },
      tenantVariant: {
        default: "",
        "oru-reco": "",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        size: "lg",
        className: "h-fit w-full max-w-110 px-8 py-4 text-lg",
      },
      {
        variant: "default",
        size: "lg",
        tenantVariant: "oru-reco",
        className: "bg-primary hover:bg-primary",
      },
      {
        variant: "secondary",
        size: "lg",
        className: "h-fit w-full max-w-110 px-8 py-4 text-lg",
      },
      {
        variant: "secondary",
        size: "lg",
        tenantVariant: "oru-reco",
        className:
          "bg-tenant-oru-website-gray hover:bg-tenant-oru-website-gray",
      },
      {
        variant: ["default", "secondary", "outline"],
        // size: "lg",
        className:
          "hover:[&_span.ce-button-children]:scale-[102.5%] hover:[&.ce-icon-left]:-ml-[0.075rem] hover:[&.ce-icon-right]:-mr-[0.075rem]",
      },
      {
        variant: "default",
        size: "lg",
        // TODO: update this font className with the right one
        className: "font-lexend",
      },
    ],
    defaultVariants: {
      tenantVariant: "default",
      size: "default",
    },
  }),
);

export interface ConEdisonButtonProps
  extends BaseButtonProps,
    VariantProps<typeof conEdisonButtonVariants> {
  tenantVariantProp?: ConEdisonTenantVariants | "default";

  IconLeft?: React.ElementType;
  iconLeftClassName?: string;

  IconRight?: React.ElementType;
  iconRightClassName?: string;

  render?: BaseButtonProps["render"];
}

export default function ConEdisonButton({
  className,
  variant = "default",
  size,
  tenantVariant = "default",
  IconLeft,
  iconLeftClassName,
  IconRight,
  iconRightClassName,
  children,
  render,
  ...props
}: ConEdisonButtonProps) {
  const isVariantScalableOnHover =
    variant === "default" || variant === "secondary" || variant === "outline";

  const baseIconClassName = cn({
    "size-6 stroke-[0.2rem] group-hover:size-[1.025rem]":
      isVariantScalableOnHover,
  });

  // FIXME: this should not be handled here. fonts should be set higher up the tree and we should only refer to them via pure className e.g. font-sans/ font-serif/ font-header/ font-mono etc
  const lexendClassName =
    (variant === "default" && size === "lg") || variant === "secondary"
      ? "" // TODO: swap for lexend.className once available in this package
      : undefined;

  return (
    <BaseButton
      {...props}
      className={cn(
        // baseButtonVariants({ variant, size }),
        conEdisonButtonVariants({ variant, size, tenantVariant }),
        lexendClassName,
        className,
      )}
      isNestedTenantComponent={true}
      render={render}
      size={size}
      variant={variant}
    >
      {IconLeft ? (
        <IconLeft className={cn(baseIconClassName, iconLeftClassName)} />
      ) : null}

      <span className="ce-button-children">{children}</span>

      {IconRight ? (
        <IconRight className={cn(baseIconClassName, iconRightClassName)} />
      ) : null}
    </BaseButton>
  );
}
